'use client';
import { useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

export default function TestimonialsPage() {
  const testimonials = [
  {
    id: 'test_joao',
    quote: "We've worked with SDSA for 5 years now, and they've never let us down. The sunflower oil quality is consistently excellent. Every shipment meets our high standards. Their team is always responsive and goes the extra mile to ensure our orders arrive on schedule.",
    name: "João Silva",
    position: "Import Manager",
    company: "Brasil Foods Ltd",
    country: "Brazil",
    countryFlag: "🇧🇷",
    product: "Sunflower Oil",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1443e6722-1763293097909.png",
    alt: "João Silva professional headshot",
    rating: 5,
    date: "January 2026"
  },
  {
    id: 'test_ahmed',
    quote: "Switching to SDSA's wood pellets boosted our energy efficiency by 15%. Their technical team helped us optimize everything for maximum performance. The quality stays consistent batch after batch, and their competitive pricing has really improved our bottom line. Excellent partnership all around.",
    name: "Ahmed Hassan",
    position: "Procurement Director",
    company: "Maroc Energy Co.",
    country: "Morocco",
    countryFlag: "🇲🇦",
    product: "Wood Pellets",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15ebdb31b-1763300068284.png",
    alt: "Ahmed Hassan professional headshot",
    rating: 5,
    date: "December 2025"
  },
  {
    id: 'test_elena',
    quote: "Our poultry farms have seen improved growth rates since using SDSA's chicken feed. The quality is consistent, and their logistics team ensures on-time delivery even in winter. They understand our seasonal needs and always deliver. We've recommended them to several other farms in our region.",
    name: "Elena Volkov",
    position: "Operations Manager",
    company: "Moscow Agricultural Supply",
    country: "Russia",
    countryFlag: "🇷🇺",
    product: "Chicken Feed",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_115b700a9-1763294224785.png",
    alt: "Elena Volkov professional headshot",
    rating: 5,
    date: "November 2025"
  },
  {
    id: 'test_pieter',
    quote: "SDSA's NPK fertilizers have transformed our crop yields. The 20-20-20 ratio is perfect for our operations. As a long-term partner, they understand our seasonal needs and always deliver ahead of schedule. Their technical expertise has helped us optimize our fertilization programs significantly.",
    name: "Pieter van der Berg",
    position: "CEO",
    company: "Netherlands Agro Supplies",
    country: "Netherlands",
    countryFlag: "🇳🇱",
    product: "NPK Fertilizers",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_179ebd6f2-1763294255544.png",
    alt: "Pieter van der Berg professional headshot",
    rating: 5,
    date: "October 2025"
  },
  {
    id: 'test_carlos',
    quote: "We've tested multiple suppliers over the years, but SDSA's palm oil quality is consistently superior. Their certifications and transparent sourcing give us complete confidence in every shipment. The customer service team is exceptional, and they handle our complex shipping requirements with ease.",
    name: "Carlos Mendes",
    position: "Purchasing Manager",
    company: "Canadian Food Distributors",
    country: "Canada",
    countryFlag: "🇨🇦",
    product: "Palm Oil",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c22a64fb-1763294195282.png",
    alt: "Carlos Mendes professional headshot", rating: 5,
    date: "September 2025"
  },
  {
    id: 'test_fatima',
    quote: "SDSA's iron scrap quality is excellent for our recycling operations. They handle large orders efficiently and their pricing is competitive. The HMS 1&2 grade we receive is consistently clean and meets all our specs. Their documentation is always accurate, which makes customs clearance smooth.",
    name: "Fatima Al-Mansoori",
    position: "Supply Chain Director",
    company: "UAE Industrial Corp.",
    country: "UAE",
    countryFlag: "🇦🇪",
    product: "Iron Scraps",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12bed7637-1763301543386.png",
    alt: "Fatima Al-Mansoori professional headshot",
    rating: 5,
    date: "August 2025"
  },
  {
    id: 'test_hans',
    quote: "Reliability is everything in our industry. SDSA delivers exactly what we need, when we need it. Their customer service is responsive and professional. We've worked together for over 3 years and have never had a single delayed shipment. The bulk cooking oil quality is perfect for our manufacturing.",
    name: "Hans Mueller",
    position: "Factory Manager",
    company: "German Manufacturing GmbH",
    country: "Germany",
    countryFlag: "🇩🇪",
    product: "Cooking Oils (Bulk)",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d3c47105-1763293003147.png",
    alt: "Hans Mueller professional headshot",
    rating: 5,
    date: "July 2025"
  },
  {
    id: 'test_olivia',
    quote: "SDSA's wood pellets are perfect for our biomass energy projects. The quality control is rigorous, and we've never had issues with shipments. Their technical team provided valuable insights on storage and handling that improved our operations. The ash content is consistently low, maximizing our energy output.",
    name: "Olivia Chen",
    position: "Procurement Specialist",
    company: "Singapore Trading Co.",
    country: "Singapore",
    countryFlag: "🇸🇬",
    product: "Wood Pellets",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17498cb9e-1763296066954.png",
    alt: "Olivia Chen professional headshot",
    rating: 5,
    date: "June 2025"
  }];


  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="py-24 bg-background">
        <div className="max-w-container mx-auto px-6 md:px-12">
          {/* Page Header */}
          <div className="text-center mb-16 reveal">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary mb-4">
              Client Success Stories
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
              What Our Partners Say
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Trusted by businesses in 25+ countries. Read authentic reviews from our global partners.
            </p>
          </div>

          {/* Featured Testimonials */}
          <div className="space-y-8 mb-16">
            {testimonials?.map((testimonial, index) =>
            <div
              key={testimonial?.id}
              className="bg-card rounded-organic-lg p-8 md:p-10 shadow-sm hover-lift reveal"
              style={{ transitionDelay: `${index * 100}ms` }}>
              
                {/* Rating */}
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(testimonial?.rating)]?.map((_, i) =>
                <Icon key={`star_${testimonial?.id}_${i}`} name="StarIcon" size={24} className="text-yellow-400 fill-current" />
                )}
                </div>

                {/* Quote */}
                <blockquote className="text-xl md:text-2xl text-foreground leading-relaxed mb-8 italic font-serif">
                  "{testimonial?.quote}"
                </blockquote>

                {/* Author Info */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <AppImage
                      src={testimonial?.image}
                      alt={testimonial?.alt}
                      className="w-20 h-20 rounded-full object-cover" />
                    
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-lg font-bold text-foreground">{testimonial?.name}</h4>
                        <span className="text-3xl" title={testimonial?.country}>{testimonial?.countryFlag}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {testimonial?.position}, {testimonial?.company}
                      </p>
                      <p className="text-sm text-primary font-semibold mt-1">
                        Product: {testimonial?.product}
                      </p>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="CalendarIcon" size={16} />
                    <span>{testimonial?.date}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Statistics Section */}
          <section className="py-16 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-organic-xl mb-16 reveal">
            <div className="px-6 md:px-12">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-foreground mb-12">
                Proven Track Record
              </h2>
              
              <div className="grid md:grid-cols-4 gap-8 text-center">
                <div>
                  <div className="text-5xl font-bold font-serif text-primary mb-2">98%</div>
                  <div className="text-sm uppercase tracking-wider text-muted-foreground">Customer Satisfaction</div>
                </div>
                <div>
                  <div className="text-5xl font-bold font-serif text-primary mb-2">5,000+</div>
                  <div className="text-sm uppercase tracking-wider text-muted-foreground">Active Partners</div>
                </div>
                <div>
                  <div className="text-5xl font-bold font-serif text-primary mb-2">25+</div>
                  <div className="text-sm uppercase tracking-wider text-muted-foreground">Countries Served</div>
                </div>
                <div>
                  <div className="text-5xl font-bold font-serif text-primary mb-2">20+</div>
                  <div className="text-sm uppercase tracking-wider text-muted-foreground">Years Experience</div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-gray-900 rounded-organic-xl p-8 md:p-12 text-center text-white reveal">
            <h3 className="text-3xl md:text-4xl font-serif font-bold mb-4">
              Join Our Growing Network
            </h3>
            <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
              Become part of our success story. Experience the quality and service that our partners trust.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-full text-base font-semibold hover:bg-primary/90 shadow-lg btn-scale">
              
              Become a Partner
              <Icon name="ArrowUpRightIcon" size={20} className="arrow-shift" />
            </Link>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );

}